import React, {useEffect, useMemo, useRef, useState} from 'react'

import {
  DOBFieldContainer,
  DOBFieldsRow,
  DOBFieldsWrapper,
  DOBPartInput,
  DOBSeparator,
  ErrorText,
} from './DOBField.styled'

export type DOBParts = {
  day: string
  month: string
  year: string
}

export type DOBChange = DOBParts & {
  isoDate: string | null
  isValid: boolean
  error: string | null
}

export type DOBFieldProps = {
  name?: string
  label?: string
  hideLabel?: boolean
  value?: DOBParts
  defaultValue?: DOBParts
  minYear?: number
  maxYear?: number
  maxDate?: string
  required?: boolean
  disabled?: boolean
  errorMessage?: string
  dayAriaLabel?: string
  monthAriaLabel?: string
  yearAriaLabel?: string
  className?: string
  onChange?: (value: DOBChange) => void
}

const EMPTY_DOB: DOBParts = {
  day: '',
  month: '',
  year: '',
}

export const isoToDOBParts = (isoDate: string): DOBParts => {
  if (!isoDate) return EMPTY_DOB
  const [year = '', month = '', day = ''] = isoDate.split('-')
  return {day, month, year}
}

const onlyDigits = (value: string, maxLength: number): string =>
  value.replace(/\D/g, '').slice(0, maxLength)

const isRealDate = (year: number, month: number, day: number): boolean => {
  const date = new Date(Date.UTC(year, month - 1, day))

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

const validateDOB = (
  value: DOBParts,
  options: {
    required: boolean
    minYear: number
    maxYear: number
    maxDate?: string
  },
): Pick<DOBChange, 'isoDate' | 'isValid' | 'error'> => {
  const {day, month, year} = value

  if (!day && !month && !year) {
    return options.required
      ? {isoDate: null, isValid: false, error: 'Enter your date of birth'}
      : {isoDate: null, isValid: true, error: null}
  }

  if (!day || !month || !year) {
    return {
      isoDate: null,
      isValid: false,
      error: 'Enter a complete date of birth',
    }
  }

  if (year.length !== 4) {
    return {
      isoDate: null,
      isValid: false,
      error: 'Enter the year with 4 digits, for example 1990',
    }
  }

  const dayNumber = Number(day)
  const monthNumber = Number(month)
  const yearNumber = Number(year)

  if (monthNumber < 1 || monthNumber > 12) {
    return {
      isoDate: null,
      isValid: false,
      error: 'Enter a month between 1 and 12',
    }
  }

  if (dayNumber < 1 || dayNumber > 31) {
    return {
      isoDate: null,
      isValid: false,
      error: 'Enter a day between 1 and 31',
    }
  }

  if (yearNumber < options.minYear || yearNumber > options.maxYear) {
    return {
      isoDate: null,
      isValid: false,
      error: `Enter a year between ${options.minYear} and ${options.maxYear}`,
    }
  }

  if (!isRealDate(yearNumber, monthNumber, dayNumber)) {
    return {
      isoDate: null,
      isValid: false,
      error: 'Enter a real date of birth',
    }
  }

  const yyyy = String(yearNumber).padStart(4, '0')
  const mm = String(monthNumber).padStart(2, '0')
  const dd = String(dayNumber).padStart(2, '0')
  const isoDate = `${yyyy}-${mm}-${dd}`

  if (options.maxDate && isoDate > options.maxDate) {
    const [maxYear, maxMonth, maxDay] = options.maxDate.split('-')
    const latestAllowedDate = `${maxDay}/${maxMonth}/${maxYear}`

    return {
      isoDate,
      isValid: false,
      error: `Enter a date of birth on or before ${latestAllowedDate}`,
    }
  }

  return {
    isoDate,
    isValid: true,
    error: null,
  }
}

export const DOBField: React.FC<DOBFieldProps> = ({
  name = 'dateOfBirth',
  label = 'Date of birth',
  hideLabel = false,
  value,
  defaultValue = EMPTY_DOB,
  minYear = 1900,
  maxYear = new Date().getFullYear(),
  maxDate,
  required = false,
  disabled = false,
  errorMessage,
  dayAriaLabel = 'Day',
  monthAriaLabel = 'Month',
  yearAriaLabel = 'Year',
  className,
  onChange,
}) => {
  const isControlled = value !== undefined
  const [internalValue, setInternalValue] = useState<DOBParts>(defaultValue)
  const [touched, setTouched] = useState(false)
  const dayRef = useRef<HTMLInputElement>(null)
  const monthRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)
  const fieldsetRef = useRef<HTMLFieldSetElement>(null)

  const currentValue = isControlled ? value : internalValue

  const validation = useMemo(
    () =>
      validateDOB(currentValue, {
        required,
        minYear,
        maxYear,
        maxDate,
      }),
    [currentValue, required, minYear, maxYear, maxDate],
  )

  const internalError = touched && !validation.isValid ? validation.error : null
  const displayError = errorMessage || internalError
  const showInvalid = Boolean(displayError)

  const emit = (nextValue: DOBParts) => {
    const nextValidation = validateDOB(nextValue, {
      required,
      minYear,
      maxYear,
      maxDate,
    })

    if (!isControlled) {
      setInternalValue(nextValue)
    }

    onChange?.({
      ...nextValue,
      ...nextValidation,
    })
  }

  const focusNext = (field: keyof DOBParts) => {
    if (field === 'day') {
      monthRef.current?.focus()
    } else if (field === 'month') {
      yearRef.current?.focus()
    }
  }

  const update = (field: keyof DOBParts, rawValue: string) => {
    const maxLength = field === 'year' ? 4 : 2
    const nextFieldValue = onlyDigits(rawValue, maxLength)

    emit({
      ...currentValue,
      [field]: nextFieldValue,
    })

    if (nextFieldValue.length === maxLength) {
      focusNext(field)
    }
  }

  const handlePartEvent =
    (field: keyof DOBParts) => (event: React.FormEvent<HTMLInputElement>) => {
      update(field, event.currentTarget.value)
    }

  const syncFromDom = () => {
    emit({
      day: onlyDigits(dayRef.current?.value ?? '', 2),
      month: onlyDigits(monthRef.current?.value ?? '', 2),
      year: onlyDigits(yearRef.current?.value ?? '', 4),
    })
  }

  const markTouchedRef = useRef<() => void>(() => {})
  markTouchedRef.current = () => {
    syncFromDom()
    setTouched(true)
  }

  const handleFieldsetBlur = () => {
    const fieldset = fieldsetRef.current
    if (!fieldset) return

    window.setTimeout(() => {
      if (!fieldset.contains(document.activeElement)) {
        markTouchedRef.current()
      }
    }, 0)
  }

  useEffect(() => {
    const fieldset = fieldsetRef.current
    if (!fieldset) return

    const handleOutsideInteraction = (event: Event) => {
      const target = event.target
      if (!(target instanceof Node) || fieldset.contains(target)) return
      if (!fieldset.contains(document.activeElement)) return
      markTouchedRef.current()
    }

    document.addEventListener('pointerdown', handleOutsideInteraction, true)
    document.addEventListener('mousedown', handleOutsideInteraction, true)
    return () => {
      document.removeEventListener(
        'pointerdown',
        handleOutsideInteraction,
        true,
      )
      document.removeEventListener('mousedown', handleOutsideInteraction, true)
    }
  }, [])

  return (
    <DOBFieldContainer className={className}>
      <DOBFieldsWrapper
        ref={fieldsetRef}
        $required={required}
        disabled={disabled}
        onBlur={handleFieldsetBlur}>
        {!hideLabel && <legend>{label}</legend>}

        <DOBFieldsRow>
          <DOBPartInput
            ref={dayRef}
            name={`${name}Day`}
            value={currentValue.day}
            onChange={handlePartEvent('day')}
            onInput={handlePartEvent('day')}
            autoComplete="bday-day"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            placeholder="DD"
            aria-label={dayAriaLabel}
            aria-invalid={showInvalid || undefined}
            disabled={disabled}
          />

          <DOBSeparator aria-hidden="true">/</DOBSeparator>

          <DOBPartInput
            ref={monthRef}
            name={`${name}Month`}
            value={currentValue.month}
            onChange={handlePartEvent('month')}
            onInput={handlePartEvent('month')}
            autoComplete="bday-month"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={2}
            placeholder="MM"
            aria-label={monthAriaLabel}
            aria-invalid={showInvalid || undefined}
            disabled={disabled}
          />

          <DOBSeparator aria-hidden="true">/</DOBSeparator>

          <DOBPartInput
            ref={yearRef}
            $wide
            name={`${name}Year`}
            value={currentValue.year}
            onChange={handlePartEvent('year')}
            onInput={handlePartEvent('year')}
            autoComplete="bday-year"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="YYYY"
            aria-label={yearAriaLabel}
            aria-invalid={showInvalid || undefined}
            disabled={disabled}
          />
        </DOBFieldsRow>

        <input
          type="hidden"
          name={name}
          value={validation.isoDate ?? ''}
          autoComplete="off"
          tabIndex={-1}
          aria-hidden="true"
        />
      </DOBFieldsWrapper>

      {showInvalid && <ErrorText size="sm">{displayError}</ErrorText>}
    </DOBFieldContainer>
  )
}
